import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { NotebookPen, Keyboard } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useNotes, useNoteMutations } from '../hooks/useNotes';
import { NoteSidebar } from '../components/NoteSidebar';
import { NoteEditor } from '../components/NoteEditor';
import { NotePropertiesPanel } from '../components/NotePropertiesPanel';
import { NOTE_VIEWS, type NoteView } from '../constants';
import { getOwnerId } from '../types';
import type { Note, SaveNotePayload } from '../types';

const RECENT_MS = 7 * 86_400_000;

export function NotesPage() {
  const user = useUser();
  const { data: notes = [], isLoading, isError } = useNotes();
  const { createNote, updateNote, deleteNote } = useNoteMutations();

  const [view, setView] = useState<NoteView>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(true);
  const [confirmNote, setConfirmNote] = useState<Note | null>(null);

  const isOwner = (n: Note) => !!user?.id && getOwnerId(n.user_id) === user.id;

  // Capture "now" once at mount so the recency comparisons stay pure during
  // render (mount-time reference is fine for the 7-day "recent" window).
  const [now] = useState(() => Date.now());

  // Per-view counts over the full dataset.
  const counts = useMemo(() => {
    const c = Object.fromEntries(NOTE_VIEWS.map((v) => [v.value, 0])) as Record<NoteView, number>;
    for (const n of notes) {
      c.all++;
      if (isOwner(n)) c.mine++;
      else c.shared++;
      if (n.updated_at && now - new Date(n.updated_at).getTime() < RECENT_MS) c.recent++;
      if (n.access_control === 'private') c.private++;
      if (n.access_control === 'user') c.users++;
      if (n.access_control === 'department') c.department++;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, user?.id, now]);

  const visible = useMemo(() => {
    let rows = notes.filter((n) => {
      switch (view) {
        case 'mine': return isOwner(n);
        case 'shared': return !isOwner(n);
        case 'recent': return !!n.updated_at && now - new Date(n.updated_at).getTime() < RECENT_MS;
        case 'private': return n.access_control === 'private';
        case 'users': return n.access_control === 'user';
        case 'department': return n.access_control === 'department';
        default: return true;
      }
    });
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((n) =>
        [n.title, n.notebookText, n.author_name].some((f) => (f ?? '').toLowerCase().includes(q)),
      );
    }
    return [...rows].sort(
      (a, b) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, view, search, user?.id, now]);

  // Keep a valid selection: pick the first visible note if the current one drops
  // out. Adjusted during render (guarded so it only runs when the value actually
  // changes) instead of in an effect.
  const validSelection = !!selectedId && visible.some((n) => n.id === selectedId);
  if (!validSelection) {
    const nextSelectedId = visible[0]?.id ?? null;
    if (nextSelectedId !== selectedId) setSelectedId(nextSelectedId);
  }

  const selectedNote = useMemo(
    () => notes.find((n) => n.id === selectedId) ?? null,
    [notes, selectedId],
  );

  const handleNew = async () => {
    try {
      const note = await createNote.mutateAsync({
        title: 'Untitled Note',
        notebookText: '',
        access_control: 'private',
        shared_with_users: [],
        shared_with_depts: [],
      });
      setView('all');
      setSearch('');
      setSelectedId(note.id);
      setPropertiesOpen(true);
    } catch {
      toast.error('Failed to create note.');
    }
  };

  // Autosave from the editor — resolves so the editor can show Saved/Failed.
  const handleEditorSave = async (id: string, payload: SaveNotePayload) => {
    await updateNote.mutateAsync({ id, payload });
  };

  // Sharing save from the properties panel.
  const handleSharingSave = (id: string, payload: SaveNotePayload) =>
    updateNote.mutate(
      { id, payload },
      {
        onSuccess: () => toast.success('Sharing updated.'),
        onError: () => toast.error('Failed to update sharing.'),
      },
    );

  const handleDelete = () => {
    if (!confirmNote) return;
    deleteNote.mutate(confirmNote.id, {
      onSuccess: () => {
        toast.success('Note deleted.');
        if (selectedId === confirmNote.id) setSelectedId(null);
        setConfirmNote(null);
      },
      onError: () => toast.error('Failed to delete note.'),
    });
  };

  return (
    <div className="flex h-full overflow-hidden">
      {!focusMode && (
        <NoteSidebar
          notes={visible}
          counts={counts}
          view={view}
          search={search}
          selectedId={selectedId}
          loading={isLoading}
          creating={createNote.isPending}
          isOwner={isOwner}
          onViewChange={setView}
          onSearchChange={setSearch}
          onSelect={(n) => setSelectedId(n.id)}
          onNew={handleNew}
        />
      )}

      {/* Center */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState title="Failed to load notes" description="Check your connection or try refreshing." />
        </div>
      ) : selectedNote ? (
        <NoteEditor
          key={selectedNote.id}
          note={selectedNote}
          canEdit={isOwner(selectedNote)}
          focusMode={focusMode}
          propertiesOpen={propertiesOpen}
          onToggleFocus={() => setFocusMode((f) => !f)}
          onToggleProperties={() => setPropertiesOpen((p) => !p)}
          onSave={handleEditorSave}
        />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-container text-on-primary-container">
            <NotebookPen className="h-8 w-8" />
          </div>
          <div>
            <h2 className="text-display-sm font-semibold text-on-surface">Your notebook awaits</h2>
            <p className="mt-1 max-w-sm text-body-md text-on-surface-variant">
              Capture ideas, share with your team, and keep everything in one place. Create your first note to begin.
            </p>
          </div>
          <Button onClick={handleNew} loading={createNote.isPending}>
            <NotebookPen className="h-4 w-4" /> Create First Note
          </Button>
          <p className="inline-flex items-center gap-1.5 text-body-sm text-on-surface-variant">
            <Keyboard className="h-3.5 w-3.5" /> Tip: notes autosave as you type
          </p>
        </div>
      )}

      {/* Right */}
      {!focusMode && propertiesOpen && selectedNote && (
        <NotePropertiesPanel
          note={selectedNote}
          canManage={isOwner(selectedNote)}
          saving={updateNote.isPending}
          deleting={deleteNote.isPending}
          onSave={handleSharingSave}
          onDelete={setConfirmNote}
        />
      )}

      <ConfirmDialog
        open={!!confirmNote}
        title="Delete note"
        message={`Delete "${confirmNote?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteNote.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmNote(null)}
      />
    </div>
  );
}
